import cn from 'classnames';
import { MenuItem } from '@blueprintjs/core';
import { ItemRendererProps, Select } from '@blueprintjs/select';
import { faCaretDown } from '@fortawesome/free-solid-svg-icons/faCaretDown';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import compare from 'es/app/business-logic/compare';
import { IFacility } from 'es/app/business-logic/domain-models/Facility';
import createKeyMap, { HotKey } from 'es/app/components/hot-keys/GlobalHotKeyMap';
import { useUserStore } from 'es/app/store/GlobalStore';
import { matchSorter } from 'match-sorter';
import { observer } from 'mobx-react';
import React, { useMemo, useRef, useState } from 'react';
import { GlobalHotKeys } from 'react-hotkeys';
import { useI18n } from 'es/core/hooks/useI18n';
import { useQueryClient } from '@tanstack/react-query';
import styles from './SiteSelector.module.css';
import { BootstrapStyles } from 'es/app/visuals/styles/BootstrapStyles';
import { FacilityCode } from 'es/app/components/layout/FacilityCode';

const HOT_KEY_MAP = createKeyMap(HotKey.SELECT_SITE);

interface ISelectableFacility extends IFacility {
    showCompanyName: boolean;
}

const FacilitySelect = Select.ofType<ISelectableFacility>();

const SiteSelector = () => {
    const { l10n } = useI18n('app/components/layout');

    const [query, setQuery] = useState('');

    const userStore = useUserStore();
    const { activeSite, accessibleFacilities: facilities } = userStore;

    const items = useSelectableFacilities(query);

    const queryClient = useQueryClient();
    const selectedFacility = facilities?.find(facility => facility.site === activeSite);
    const handleFacilityChanged = (facility: ISelectableFacility) => {
        userStore.setActiveFacility(facility);
        queryClient.refetchQueries();
    };

    // The active item is just the one that's highlighted but not necessarily the one that was last selected
    // (i.e. this caters for the user going up/down with the arrow keys)
    const [activeItem, setActiveItem] = useState<ISelectableFacility | null>(null);
    const handleActiveItemChange = (item: ISelectableFacility | null) => setActiveItem(item);

    const [isPopupOpen, setIsPopupOpen] = useState(false);
    const handlePopupClosing = () => {
        setQuery('');
        setIsPopupOpen(false);
    };

    const handlePopupOpening = () => {
        setActiveItem(() => items.find(facility => facility.site === activeSite) ?? null);
        setIsPopupOpen(true);
    };

    const buttonRef = useRef<HTMLDivElement>(null);

    const handlers = {
        [HotKey.SELECT_SITE]: () => buttonRef.current?.click(),
    };

    const buttonClassNames = cn(styles.siteSelectorButton, {
        [styles.active ?? '']: isPopupOpen,
    });

    return (
        <>
            <GlobalHotKeys keyMap={HOT_KEY_MAP} handlers={handlers} />

            <FacilitySelect
                items={items}
                itemRenderer={renderFacility}
                onItemSelect={handleFacilityChanged}
                activeItem={activeItem}
                onActiveItemChange={handleActiveItemChange}
                query={query}
                onQueryChange={handleQueryChange}
                scrollToActiveItem={true}
                popoverProps={{
                    minimal: true,
                    portalClassName: styles.siteSelector,
                    onOpening: handlePopupOpening,
                    onClosing: handlePopupClosing,
                }}
            >
                {selectedFacility && renderSelectedFacility(selectedFacility)}
            </FacilitySelect>

            <FacilityCode facility={selectedFacility} />
        </>
    );

    function renderSelectedFacility(facility: IFacility) {
        if (!facility) {
            return (
                <div className={buttonClassNames} ref={buttonRef}>
                    - {l10n('selection.noAvailableFacilities')} -
                </div>
            );
        }
        return (
            <div className={buttonClassNames} ref={buttonRef}>
                {`${selectedFacility?.companyName} - ${selectedFacility?.name}`}
                <FontAwesomeIcon icon={faCaretDown} className={BootstrapStyles['ms-2']} />
            </div>
        );
    }

    function renderFacility(facility: ISelectableFacility, { handleClick, modifiers }: ItemRendererProps) {
        return (
            <React.Fragment key={facility.id}>
                {facility.showCompanyName && <h6>{facility.companyName}</h6>}

                <MenuItem
                    active={modifiers.active}
                    disabled={modifiers.disabled}
                    onClick={handleClick}
                    text={
                        <>
                            <span className={BootstrapStyles['px-2']}>{facility.name}</span>
                        </>
                    }
                />
            </React.Fragment>
        );
    }

    ////////////////////

    function handleQueryChange(query: string) {
        setQuery(query.toLocaleLowerCase());
    }
};

function useSelectableFacilities(query: string) {
    const { accessibleFacilities: facilities } = useUserStore();
    return useMemo((): ISelectableFacility[] => {
        const filteredItems = !query
            ? facilities
            : matchSorter(facilities ?? [], query, {
                  keys: ['companyName', 'name', 'site.companyAlias', 'site.facilityAlias'],
                  threshold: matchSorter.rankings.CONTAINS,
              });

        const compareByCompanyName = compare.byStringValue<IFacility>(c => c.companyName);

        return [...(filteredItems ?? [])]
            .sort((left, right) => {
                const companyCompareResult = compareByCompanyName(left, right);
                return companyCompareResult !== 0 ? companyCompareResult : compare.byName(left, right);
            })
            .map((facility, index, source) => {
                let showCompanyName = index === 0;
                if (!showCompanyName) {
                    const prevFacility = source[index - 1];
                    showCompanyName = facility.companyId !== prevFacility?.companyId;
                }

                return {
                    ...facility,
                    showCompanyName,
                };
            });
    }, [facilities, query]);
}

export default observer(SiteSelector);
